import { Children, type PropsWithChildren, type ReactNode } from 'react';
import { isElement } from 'react-is';

import { Intent, IntentProps } from '../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import { ColorRule } from '../../core/slots/ColorRule.js';
import { ChartToolbar } from '../../core/slots/toolbar/ChartToolbar.js';
import { sanitizeProps } from '../../core/slots/toolbar/CompactToolbar.js';
import { DownloadData } from '../../core/slots/toolbar/DownloadData.js';
import type { ChartToolbarProps } from '../../core/types/toolbar.js';
import { extractBehaviorTrackingProps } from '../../core/utils/extract-behavior-tracking-props.js';
import { TreeMapDownloadCSVSlot } from '../slots/DownloadCSV.js';
import { Toolbar } from '../slots/Toolbar.js';
import { Tooltip } from '../slots/Tooltip.js';
import { Legend as TreeMapLegend } from '../slots/TreeMapChartLegend.js';
import {
  TreemapChartLegendConfig,
  TreeMapChartSlots,
  type TreeMapChartToolbarConfig,
} from '../types/treemap-chart.config.js';

/**
 * Generate chart config based on slots
 * @param children - CharInteraction children
 * @returns ChartInteraction slot config
 */
const iterateChartToolbarSlots = (
  children: ReactNode,
): TreeMapChartToolbarConfig => {
  let slots = {};

  Children.forEach(children, (childInteractions) => {
    if (isElement(childInteractions)) {
      switch (childInteractions.type) {
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        case DownloadData:
          slots = { ...slots, downloadData: { enabled: true } };
          break;
      }
    }
  });

  return slots;
};

/**
 * Iterate over the slotted children to merge the configuration.
 * @param children - Slotted children to iterate
 * @returns Merged config as TreemapChartSlots
 */
export function iterateSlots(children: ReactNode): TreeMapChartSlots {
  let slots: TreeMapChartSlots = {
    legend: {},
    tooltip: false,
    toolbar: undefined,
    colorRules: [],
  };

  let intents: IntentProps[] | undefined;

  Children.map(children, (child) => {
    if (isSlot(child, Tooltip)) {
      slots = { ...slots, tooltip: true };
    } else if (isSlot(child, TreeMapLegend)) {
      const legend = child.props as TreemapChartLegendConfig;

      slots = { ...slots, legend };
      // eslint-disable-next-line @typescript-eslint/no-deprecated
    } else if (isSlot(child, ChartToolbar)) {
      const { children, ...toolbarProps } =
        child.props as PropsWithChildren<ChartToolbarProps>;

      const toolbarSlots = iterateChartToolbarSlots(children);

      const toolbar = { ...toolbarProps, ...toolbarSlots };
      slots = { ...slots, toolbar };
    } else if (isSlot(child, ColorRule)) {
      const colorRule = child.props;
      if (slots.colorRules) {
        slots.colorRules.push(colorRule);
      }
    } else if (isSlot(child, Intent)) {
      intents = intents ? [...intents, child.props] : [child.props];
    } else if (isSlot(child, Toolbar)) {
      slots = {
        ...slots,
        toolbar: {
          ...slots.toolbar,
          ...sanitizeProps(child.props),
          ...extractBehaviorTrackingProps(child.props),
        },
      };
    } else if (isSlot(child, TreeMapDownloadCSVSlot)) {
      slots = {
        ...slots,
        toolbar: {
          ...slots.toolbar,
          downloadData: { enabled: true },
        },
      };
    }
  });

  if (intents?.length) {
    slots = {
      ...slots,
      toolbar: {
        ...slots.toolbar,
        intents,
      },
    };
  }

  return slots;
}
